import React, { useMemo, useState, useEffect } from 'react';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import api from '../../../middleware/api';
import Search from '../../Common/DaisyTable/Search/Search';
import ConfigurationDetails from './ConfigurationDetails';
import { toast } from 'react-toastify';
import { useBreadcrumbs } from '../../../context/BreadcrumbsContext';
import LoadingSpinner from '../../Common/Loading/LoadingSpinner';
import { formatDateOnly } from '../../../utils/dateFormatter';

const AllCapsules = () => {
    const [capsules, setCapsules] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState({ value: '', keys: [] });
    const [searchType, setSearchType] = useState('global');
    const [columnsVisibility, setColumnsVisibility] = useState({});
    const [expandedRows, setExpandedRows] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const { updateLastBreadcrumb } = useBreadcrumbs();

    useEffect(() => {
        updateLastBreadcrumb('Liste des Capsules');
    }, []);

    useEffect(() => {
        const fetchCapsules = async () => {
            try {
                setIsLoading(true);
                const response = await api.get('/capsules/user');
                setCapsules(response.data);
            } catch (error) {
                console.error('Erreur lors de la récupération des capsules:', error);
                toast.error('Erreur lors de la récupération des capsules');
            } finally {
                setIsLoading(false);
            }
        };
        fetchCapsules();
    }, []);

    const columns = useMemo(() => [
        {
            id: 'name',
            accessorKey: 'name',
            header: () => <span className="font-semibold">Nom</span>,
            cell: info => <span className="font-medium">{info.getValue()}</span>,
        },
        {
            id: 'customer',
            accessorFn: (row) => (row.customer.firstname || '') + ' ' + (row.customer.lastname || ''),
            header: () => <span className="font-semibold">Client</span>,
            cell: info => <span>{info.getValue()}</span>
        },
        {
            id: 'nb_mixtures',
            accessorFn: (row) => row.configurations[0].configuration.total || 0,
            header: () => <span className="font-semibold">Nombre de mélanges</span>,
            cell: info => (
                <div className="badge badge-primary">
                    {info.getValue()}
                </div>
            )
        },
        {
            id: 'creationDate',
            accessorKey: 'creationDate',
            header: () => <span className="font-semibold">Date de Création</span>,
            cell: info => (
                <span className="text-sm text-gray-600">
                    {formatDateOnly(info.getValue())}
                </span>
            ),
        },
        {
            id: 'expander',
            header: () => null,
            cell: ({ row }) => (
                <button 
                    className="btn btn-ghost btn-xs"
                    onClick={(e) => { 
                        e.stopPropagation(); 
                        toggleRowExpansion(row.id);
                    }}
                    aria-label={expandedRows[row.id] ? "Réduire les détails" : "Afficher les détails"}
                >
                    {expandedRows[row.id] ? (
                        <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                        <ChevronRightIcon className="w-4 h-4" />
                    )}
                </button>
            ),
            enableGlobalFilter: false,
        },
    ], [expandedRows]);

    const filteredCapsules = useMemo(() => {
        return capsules.filter(capsule => capsule.configurations[0].configuration.total > 0);
    }, [capsules]);

    const table = useReactTable({
        data: filteredCapsules,
        columns,
        state: { sorting, globalFilter, columnsVisibility },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onColumnsVisibilityChange: setColumnsVisibility,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const toggleRowExpansion = (rowId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [rowId]: !prev[rowId],
        }));
    };

    const headers = table.getFlatHeaders();
    const rows = table.getRowModel().rows;

    if (isLoading) {
        return (
           <LoadingSpinner style="medium"/>
        );
    }

    return (
        <div className="overflow-hidden p-4 md:p-8">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Liste des Capsules</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Gérez et consultez toutes vos capsules
                    </p>
                </div>
          
            </div>

            <div className=" p-4 mb-6 flex justify-between items-center">
                <Search
                    columns={columns}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    searchType={searchType}
                    setSearchType={setSearchType}
                    columnsVisibility={columnsVisibility}
                    setColumnsVisibility={setColumnsVisibility}
                />
                      <div className="badge badge-primary badge-lg">
                    {filteredCapsules.length} capsules
                </div>
            </div>

            <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
                <table className="table table-zebra w-full">
                    <thead>
                        <tr>
                            {headers.map(header => (
                                <th
                                    key={header.id}
                                    className="bg-base-200"
                                    style={{
                                        width: header.id === 'expander' ? '48px' : header.getSize(),
                                        cursor: 'pointer',
                                    }}
                                    onClick={header.column.getToggleSortingHandler()}
                                >
                                    <div className="flex items-center gap-2">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {header.column.getIsSorted() && (
                                            <span className="text-xs">
                                                {header.column.getIsSorted() === 'asc' ? <ArrowUpIcon className='w-4 h-4' /> : <ArrowDownIcon className='w-4 h-4' />}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <React.Fragment key={row.id}>
                                <tr 
                                    onClick={() => toggleRowExpansion(row.id)} 
                                    className="hover:bg-base-200 cursor-pointer transition-colors"
                                >
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} className={cell.column.id === 'expander' ? 'w-12' : ''}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                                {expandedRows[row.id] && (
                                    <tr>
                                        <td colSpan={row.getVisibleCells().length}>
                                            <div className="p-4 bg-base-200">
                                                <div className="grid grid-cols-1gap-4">
                                                    <div>
                                                        <h3 className="text-lg font-semibold mb-2">
                                                            Détails de la capsule : {row.original.name}
                                                        </h3>
                                                        <div className="space-y-2 text-sm">
                                                            <p>
                                                                <span className="font-medium">Date de Création:</span>{' '}
                                                                {formatDateOnly(row.original.creationDate)}
                                                            </p>
                                                            <p>
                                                                <span className="font-medium">Client:</span>{' '}
                                                                {row.original.customer.firstname} {row.original.customer.lastname}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        
                                                        <ConfigurationDetails 
                                                            data={row.original.configurations[0]} 
                                                            configurations={row.original.configurations} 
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default AllCapsules;
